import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Search, Users, MousePointerClick, TrendingUp, Crown, Sparkles, Target } from "lucide-react";
import { useProfileMetrics } from "@/hooks/useProfileMetrics";

interface UnifiedTrainerMetricsProps {
  trainerId: string;
  trainerUserId: string;
  isPremium: boolean;
  views?: number;
}

export const UnifiedTrainerMetrics = ({
  trainerId,
  trainerUserId,
  isPremium,
  views = 0
}: UnifiedTrainerMetricsProps) => {
  const { loading, isOwner, isAuthenticated, navigate, metrics } = useProfileMetrics({
    type: 'trainer',
    profileId: trainerId,
    profileUserId: trainerUserId
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Only show to owner
  if (!isOwner) {
    return null;
  }

  const premiumMultiplier = isPremium ? 1 : 4;
  const potentialViews = metrics.profileViews * premiumMultiplier;
  const potentialContacts = metrics.contactClicks.total * premiumMultiplier;

  return (
    <Card className="border-primary/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Rendimiento de tu Perfil
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <Search className="w-3 h-3" />
              <span>Búsquedas</span>
            </div>
            <p className="text-2xl font-bold">{metrics.searchAppearances}</p>
            <p className="text-xs text-muted-foreground">este mes</p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <Eye className="w-3 h-3" />
              <span>Visitas</span>
            </div>
            <p className="text-2xl font-bold">{metrics.profileViews.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">mensuales</p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <Users className="w-3 h-3" />
              <span>Únicos</span>
            </div>
            <p className="text-2xl font-bold">{metrics.uniqueVisitors}</p>
            <p className="text-xs text-muted-foreground">visitantes</p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <MousePointerClick className="w-3 h-3" />
              <span>Contactos</span>
            </div>
            <p className="text-2xl font-bold">{metrics.contactClicks.total}</p>
            <p className="text-xs text-muted-foreground">clicks</p>
          </div>
        </div>

        {/* Premium Comparison */}
        {!isPremium && (
          <div className="bg-gradient-to-r from-primary/10 via-accent/5 to-primary/10 rounded-lg p-4 border border-primary/20">
            <div className="flex items-start gap-3 mb-3">
              <Crown className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold mb-1 flex items-center gap-2">
                  Con Premium obtendrías:
                  <Badge variant="default" className="bg-gradient-primary">
                    {premiumMultiplier}× más clientes
                  </Badge>
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm mt-3">
                  <div>
                    <span className="text-muted-foreground">Visitas mensuales:</span>
                    <p className="font-bold text-lg">{potentialViews}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Contactos:</span>
                    <p className="font-bold text-lg">{potentialContacts}</p>
                  </div>
                </div>
              </div>
            </div>
            <Button 
              className="w-full bg-gradient-primary mt-3"
              onClick={() => navigate('/become-trainer')}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Actualizar a Premium
            </Button>
          </div>
        )}

        {/* Growth Tips for Low Performers */}
        {!isPremium && metrics.profileViews < 50 && (
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Target className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <h4 className="font-semibold mb-2">💡 Aumenta tu visibilidad:</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>✓ Completa tu galería de fotos (8+ imágenes)</li>
                  <li>✓ Añade video de presentación</li>
                  <li>✓ Actualiza tus especialidades y certificaciones</li>
                  <li>✓ Considera premium para aparecer destacado</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
